// Setting up one FreeRADIUS server with the AuthNull bridge: the steps shown after
// "Add RADIUS server" on the RADIUS Servers page (/pam/radius/servers), and again from a
// server's "Download app.env".
//
// OLD: this file was the "Connect FreeRADIUS" page at /pam/radius/onboard (antd Steps,
// 75% width, hard-coded greys). That route now redirects to RADIUS Servers, and the page's
// steps live on here for one registered server, so app.env carries its RADIUS_SERVER_ID.
// Dropped with the page:
//   - "Step 4: Add your network devices / Next: Add Radius Device": devices register
//     themselves on their first login now; a note links to Network Devices instead.
//   - "Already using another RADIUS server?" cards (Microsoft NPS, Cisco ISE, ClearPass)
//     and their forwarding guide: NPS / ISE / ClearPass setup flows are not in this release.
//   - The Directory was required before downloading: DOMAIN is optional in the bridge now
//     (empty: AuthNull looks up each user's own directory).
import React, { useEffect, useState } from "react";
import { Button, Input, Select, message } from "antd";
import { Download, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";

import { ORG_ID, DOMAIN_ID } from "../../constants";
import { getAllADDomains } from "../../service/adPolicyService";
import { domainFqdn, domainLabel } from "../../pages/enduser/ad-policies/adPolicyModel";
import { AN_COLORS, AN_MONO } from "../../design-system/tokens";
import { CodeBlock, FieldLabel, Note } from "../../pages/enduser/ssh-proxy/sshProxyUi";
import { BRIDGE_INSTALL_COMMAND, buildBridgeEnvFile, defaultBridgeApiUrl } from "./radiusDeviceModel";

const TEST_COMMAND = 'echo "User-Name=<user>,User-Password=<password>" | radclient -t 90 -r 1 127.0.0.1:1812 auth <secret>';
// freeradius-utils carries radclient, which the test step uses.
const FREERADIUS_INSTALL_COMMAND = "sudo apt install freeradius freeradius-utils";
const RESTART_COMMAND = "sudo systemctl restart freeradius";

// Saves text as a file in the browser.
export function downloadTextFile(fileName, content) {
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const Path = ({ children }) => (
  <code style={{ fontFamily: AN_MONO, fontSize: "0.75rem", color: AN_COLORS.ink2, wordBreak: "break-all" }}>{children}</code>
);

const Hint = ({ children }) => (
  <div style={{ fontSize: "0.78rem", lineHeight: 1.5, color: AN_COLORS.muted, marginTop: "0.4rem" }}>{children}</div>
);

// One numbered step.
function SetupStep({ n, title, children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.6rem minmax(0, 1fr)", gap: "0.75rem", marginBottom: "1.4rem" }}>
      <span
        style={{
          width: "1.6rem",
          height: "1.6rem",
          borderRadius: "50%",
          background: AN_COLORS.primaryL,
          color: AN_COLORS.primary,
          fontFamily: AN_MONO,
          fontSize: "0.75rem",
          fontWeight: 600,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {n}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: "0.9rem", fontWeight: 600, color: AN_COLORS.ink, marginBottom: "0.6rem", lineHeight: "1.6rem" }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

// server: the registered server ({ id, name }) whose app.env this downloads.
export default function RadiusServerSetup({ server }) {
  const orgId = ORG_ID();
  const tenantId = DOMAIN_ID();
  const [notification, contextHolder] = message.useMessage();
  const [domains, setDomains] = useState([]);
  const [domainId, setDomainId] = useState(undefined);
  const [apiBaseUrl, setApiBaseUrl] = useState(defaultBridgeApiUrl(window.location.hostname));

  useEffect(() => {
    if (!orgId) return;
    getAllADDomains({
      payload: { orgId, tenantId },
      onSuccess: (res) => setDomains(res?.domains || res?.data || []),
      // The directory is optional, so the steps still work without the list.
      onFailure: () => setDomains([]),
    });
  }, [orgId, tenantId]);

  const domain = domainFqdn(domains.find((d) => d.id === domainId)) || "";
  const canDownload = !!(orgId && server?.id && apiBaseUrl.trim());

  const downloadEnvFile = () => {
    downloadTextFile(
      "app.env",
      buildBridgeEnvFile({ apiBaseUrl, orgId, tenantId, domain, radiusServerId: server?.id }),
    );
    notification.success({ content: `app.env for ${server?.name || "this server"} downloaded.` });
  };

  return (
    <div style={{ color: AN_COLORS.ink }}>
      {contextHolder}
      {!orgId && (
        <div style={{ marginBottom: "1rem" }}>
          <Note tone="danger" icon={<TriangleAlert size={15} />}>
            Your session has no organisation. Sign in again before downloading app.env.
          </Note>
        </div>
      )}

      <SetupStep n={1} title="Download app.env">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(13rem, 1fr))", gap: "0.75rem" }}>
          <div>
            <FieldLabel hint="optional">Directory</FieldLabel>
            <Select
              allowClear
              style={{ width: "100%" }}
              value={domainId}
              onChange={setDomainId}
              placeholder="Each user's own directory"
              notFoundContent="No directory found."
              options={domains.map((d) => ({ value: d.id, label: domainLabel(d) || domainFqdn(d) }))}
            />
          </div>
          <div>
            <FieldLabel required>AuthNull address</FieldLabel>
            <Input value={apiBaseUrl} onChange={(e) => setApiBaseUrl(e.target.value)} placeholder="http://10.0.0.9:2882" />
          </div>
        </div>
        <Hint>
          AuthNull address: how your FreeRADIUS server reaches AuthNull, on port 2882. It starts with this page&apos;s
          host, which is not always how FreeRADIUS reaches AuthNull. Directory: set it only when every username
          without a domain belongs to one directory.
        </Hint>
        <Button
          type="primary"
          style={{ marginTop: "0.75rem" }}
          icon={<Download size={14} />}
          onClick={downloadEnvFile}
          disabled={!canDownload}
        >
          Download app.env
        </Button>
        <Hint>
          Tells the bridge its organisation, tenant and this server&apos;s id (RADIUS_SERVER_ID), which its heartbeat
          and every login carry.
        </Hint>
      </SetupStep>

      <SetupStep n={2} title="Install the bridge on your FreeRADIUS server">
        <FieldLabel>Install FreeRADIUS (Ubuntu / Debian)</FieldLabel>
        <CodeBlock copyText={FREERADIUS_INSTALL_COMMAND} multiline>
          {FREERADIUS_INSTALL_COMMAND}
        </CodeBlock>
        <div style={{ height: "0.9rem" }} />
        <FieldLabel>Copy app.env to the server and run this in the same folder</FieldLabel>
        <CodeBlock copyText={BRIDGE_INSTALL_COMMAND} multiline>
          {BRIDGE_INSTALL_COMMAND}
        </CodeBlock>
        <Hint>
          <Path>--ad</Path> also connects FreeRADIUS to Active Directory and tests a login: it asks for the domain
          controller and a service account. FreeRADIUS checks the password; AuthNull adds the push.
        </Hint>
      </SetupStep>

      <SetupStep n={3} title="Restart FreeRADIUS">
        <CodeBlock copyText={RESTART_COMMAND} multiline>
          {RESTART_COMMAND}
        </CodeBlock>
        <Hint>On RHEL the service is called radiusd.</Hint>
      </SetupStep>

      <SetupStep n={4} title="Test a login">
        <CodeBlock copyText={TEST_COMMAND} multiline>
          {TEST_COMMAND}
        </CodeBlock>
        <Hint>
          &lt;secret&gt; is the secret of the localhost client in <Path>/etc/freeradius/3.0/clients.conf</Path> (testing123
          on a fresh install). Logs: <Path>/var/log/authnull/radius-bridge.log</Path>
        </Hint>
      </SetupStep>

      <Note tone="info">
        {server?.name || "The server"} shows as Inactive / Never connected until its first heartbeat, about a minute
        after the install. Then add each VPN, switch or firewall to <Path>clients.conf</Path> on this server; it appears
        under{" "}
        <Link to="/pam/radius" style={{ color: "inherit", fontWeight: 600, textDecoration: "underline" }}>
          Network Devices
        </Link>{" "}
        after its first login.
      </Note>
    </div>
  );
}
